import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import toast from 'react-hot-toast';
import { PackageCheck, Search } from 'lucide-react';

import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import {
  useSmartBack,
  shouldShowModuleHomeBack
} from '../../../shared/navigation';
import {
  createGoodsReceivedNote,
  getDailyRequirementById,
  getDailyRequirementsByProject,
  getGoodsReceivedNotes,
  uploadFilesToS3
} from '../../services/api/implementation/impl';

const RECEIVABLE_STATUSES = new Set(['Issued', 'Partial Issued', 'Partial Completed']);

const SiteMaterialReceipts = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { collapsed } = useSidebar();
  const userId = Cookies.get('userId');
  const { onBack } = useSmartBack({ fallback: '/builder/inventory' });
  const showHomeBack = shouldShowModuleHomeBack('inventory', location);
  const { projects, projectsForDropdown } = useProjectsList();
  const queryClient = useQueryClient();

  const initialProjectId = Number(
    location.state?.projectID ||
      localStorage.getItem('inventory_site_receipts_project') ||
      projects?.[0]?.id ||
      0
  );
  const [projectId, setProjectId] = useState(initialProjectId || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState(
    location.state?.dailyRequirementId ? Number(location.state.dailyRequirementId) : null
  );
  const [itemQuantities, setItemQuantities] = useState({});
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!projectId && projects?.[0]?.id) {
      setProjectId(projects[0].id);
    }
  }, [projectId, projects]);

  useEffect(() => {
    if (projectId) {
      localStorage.setItem('inventory_site_receipts_project', String(projectId));
    }
  }, [projectId]);

  useEffect(() => {
    mixpanel.track('Site Material Receipts Viewed', {
      user_id: userId,
      project_id: projectId || null
    });
  }, [userId, projectId]);

  const enabled = Boolean(projectId);

  const { data: listRes, isFetching: loadingList } = useQuery({
    queryKey: ['site-receipts-daily-requirements', projectId],
    queryFn: () => getDailyRequirementsByProject(projectId, { limit: 200, page: 1 }),
    enabled
  });

  const receivableRows = useMemo(() => {
    const rows = listRes?.data || [];
    return rows.filter((row) => RECEIVABLE_STATUSES.has(String(row.status || '')));
  }, [listRes]);

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return receivableRows;
    const q = searchQuery.trim().toLowerCase();
    return receivableRows.filter((row) => {
      const haystack = `${row.id} ${row.status} ${row.task?.name || ''} ${row.user?.name || ''} ${row.comment || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [receivableRows, searchQuery]);

  const { data: detail, isFetching: loadingDetail } = useQuery({
    queryKey: ['site-receipt-detail', selectedId],
    queryFn: () => getDailyRequirementById(selectedId),
    enabled: Boolean(selectedId)
  });

  const { data: grnHistoryRes, isFetching: loadingHistory } = useQuery({
    queryKey: ['site-receipt-grns', selectedId],
    queryFn: () => getGoodsReceivedNotes({ dailyRequirement_id: selectedId, limit: 50, page: 1 }),
    enabled: Boolean(selectedId)
  });

  useEffect(() => {
    if (!detail?.dailyRequirementItems) return;
    const next = {};
    detail.dailyRequirementItems.forEach((item) => {
      next[item.id] = '';
    });
    setItemQuantities(next);
    setFiles([]);
  }, [detail?.id]);

  const closeModal = () => {
    setSelectedId(null);
    setItemQuantities({});
    setFiles([]);
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      const items = (detail?.dailyRequirementItems || [])
        .map((item) => ({
          dailyRequirementItem_id: item.id,
          quantity: Number(itemQuantities[item.id])
        }))
        .filter((item) => Number.isFinite(item.quantity) && item.quantity > 0);

      if (!items.length) {
        throw new Error('Enter quantity for at least one item');
      }
      if (!files.length) {
        throw new Error('Upload at least one photo as proof');
      }

      setSubmitting(true);
      const uploaded = await uploadFilesToS3(Array.from(files));
      return createGoodsReceivedNote({
        dailyRequirement_id: detail.id,
        images: uploaded.map((file) => ({ name: file.name, url: file.url })),
        items
      });
    },
    onSuccess: () => {
      toast.success('Site receipt saved');
      mixpanel.track('Site GRN Created', {
        user_id: userId,
        dailyRequirement_id: detail?.id,
        project_id: Number(projectId)
      });
      closeModal();
      queryClient.invalidateQueries({ queryKey: ['site-receipts-daily-requirements', projectId] });
      queryClient.invalidateQueries({ queryKey: ['site-receipt-grns'] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save site receipt');
    },
    onSettled: () => setSubmitting(false)
  });

  return (
    <Layout>
      <Loader isVisible={loadingList || submitting} />
      <div
        className={`fixed top-0 z-10 bg-white ${
          collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
        }`}>
        <Header
          data={{ name: 'Site Material Receipts' }}
          backArrow={showHomeBack}
          onBack={showHomeBack ? onBack : undefined}
        />
      </div>

      <div className="px-6 pb-8 pt-[5.5rem]">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-[#1E4A53]">Confirm issued material on site</h2>
            <p className="text-sm text-gray-500">
              Manual qty + photos for warehouse issues (Issued / Partial).
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={projectId || ''}
              onChange={(event) => setProjectId(event.target.value)}
              className="h-10 min-w-[12rem] rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800">
              {(projectsForDropdown || []).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.label || project.name}
                </option>
              ))}
            </select>
            <label className="flex h-10 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3">
              <Search size={16} className="text-gray-400" />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search requirement / task"
                className="w-52 bg-transparent text-sm outline-none"
              />
            </label>
            <button
              type="button"
              onClick={() =>
                navigate('/builder/inventory/pipeline', {
                  state: { projectID: Number(projectId) }
                })
              }
              className="h-10 rounded-lg border border-[#2A565E] px-4 text-sm font-semibold text-[#2A565E]">
              Open pipeline
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
            <PackageCheck size={18} className="text-[#2A565E]" />
            <h3 className="text-sm font-semibold text-[#1E4A53]">
              Receivable issues ({filteredRows.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[#F4F7F8] text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">Task</th>
                  <th className="px-4 py-3">Requested by</th>
                  <th className="px-4 py-3">Items</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                      No issued material waiting for site receipt
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row) => (
                    <tr key={row.id} className="border-t border-gray-100">
                      <td className="px-4 py-3 font-medium text-gray-900">#{row.id}</td>
                      <td className="px-4 py-3">{row.task?.name || row.task_id || '—'}</td>
                      <td className="px-4 py-3">{row.user?.name || '—'}</td>
                      <td className="px-4 py-3">
                        {row.dailyRequirementItems?.length || 0}
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
                          {row.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setSelectedId(row.id)}
                          className="rounded-lg bg-[#2A565E] px-3 py-1.5 text-xs font-semibold text-white">
                          Receive
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedId && (
        <Modal
          name={`Site receive — #${selectedId}`}
          width={52}
          onClose={closeModal}>
          <Loader isVisible={loadingDetail || loadingHistory} />
          {!detail ? (
            <p className="text-sm text-gray-500">Loading requirement…</p>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                <p>
                  Status: <span className="font-semibold">{detail.status}</span>
                </p>
                <p className="mt-1">Task: {detail.task?.name || detail.task_id || '—'}</p>
              </div>

              <div className="space-y-3">
                {(detail.dailyRequirementItems || []).map((item) => (
                  <div
                    key={item.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-100 p-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {item.item?.item_name || `Item #${item.item_id}`}
                      </p>
                      <p className="text-xs text-gray-500">
                        Issued qty: {item.quantity} {item.item?.unit || ''}
                      </p>
                    </div>
                    <label className="text-sm">
                      <span className="mr-2 text-gray-600">Received</span>
                      <input
                        type="number"
                        min={0}
                        max={item.quantity}
                        value={itemQuantities[item.id] ?? ''}
                        onChange={(e) =>
                          setItemQuantities((prev) => ({
                            ...prev,
                            [item.id]: e.target.value
                          }))
                        }
                        className="h-9 w-24 rounded-lg border border-gray-200 px-2 outline-none focus:border-[#2A565E]"
                      />
                    </label>
                  </div>
                ))}
              </div>

              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Photos (required)</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setFiles(e.target.files || [])}
                  className="block w-full text-sm text-gray-600"
                />
              </label>

              {(grnHistoryRes?.data || []).length > 0 && (
                <div>
                  <p className="mb-2 text-sm font-semibold text-gray-800">Previous receipts</p>
                  <div className="space-y-2">
                    {(grnHistoryRes.data || []).map((note) => (
                      <div
                        key={note.id}
                        className="rounded border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                        GRN #{note.id} · {(note.items || []).length} line(s)
                        {note.images?.[0]?.url ? (
                          <a
                            href={note.images[0].url}
                            target="_blank"
                            rel="noreferrer"
                            className="ml-2 text-[#2A565E] underline">
                            photo
                          </a>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700">
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => submitMutation.mutate()}
                  className="rounded-lg bg-[#2A565E] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                  {submitting ? 'Saving…' : 'Confirm site receive'}
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </Layout>
  );
};

export default SiteMaterialReceipts;
